import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Users, MapPin, LogIn, LogOut, Clock, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [todayRecords, setTodayRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, recordsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/time-records/today`)
      ]);
      setStats(statsRes.data);
      setTodayRecords(recordsRes.data.slice(0, 10));
    } catch (error) {
      toast.error("Erro ao carregar dados do dashboard");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8 animate-fade-in" data-testid="admin-dashboard">
        {/* Welcome Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-800 font-manrope">
            Olá, {user?.name}!
          </h1>
          <p className="text-gray-500 mt-1">
            Aqui está o resumo de hoje
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="stat-card" data-testid="stat-workers">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Trabalhadores</p>
                  <p className="stat-value">{stats?.total_workers || 0}</p>
                  <p className="text-xs text-gray-400 mt-1">máx. 20</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-amber-100 to-amber-50 flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#F5A623]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="stat-locations">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Locais</p>
                  <p className="stat-value">{stats?.total_locations || 0}</p>
                  <p className="text-xs text-gray-400 mt-1">máx. 20</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-100 to-green-50 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-[#2ECC40]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="stat-entries">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Entradas Hoje</p>
                  <p className="stat-value">{stats?.today_entries || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center">
                  <LogIn className="w-6 h-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="stat-active">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Ativos Agora</p>
                  <p className="stat-value text-[#2ECC40]">{stats?.active_workers || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-100 to-green-50 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-[#2ECC40]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card className="bg-white border border-gray-100 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-gray-800">
              Atividade Recente
            </CardTitle>
            <Button 
              variant="ghost" 
              className="text-[#F5A623] hover:text-[#D99015] hover:bg-amber-50"
              onClick={() => navigate("/admin/records")}
              data-testid="view-all-records-btn"
            >
              Ver todos <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {todayRecords.length === 0 ? (
              <div className="empty-state py-12">
                <Clock className="empty-state-icon" />
                <p className="empty-state-title">Nenhum registro hoje</p>
                <p className="empty-state-description">
                  Os registros de ponto aparecerão aqui
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {todayRecords.map((record) => (
                  <div
                    key={record.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    data-testid={`record-${record.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        record.type === "entry" 
                          ? "bg-green-100 text-[#2ECC40]" 
                          : "bg-amber-100 text-[#F5A623]"
                      }`}>
                        {record.type === "entry" ? <LogIn size={18} /> : <LogOut size={18} />}
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{record.worker_name}</p>
                        <p className="text-sm text-gray-500">
                          {record.type === "entry" ? "Entrada" : "Saída"} em {record.location_name}
                        </p>
                      </div>
                    </div>
                    <span className="text-sm text-gray-400 font-mono">
                      {formatTime(record.timestamp)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Button
            onClick={() => navigate("/admin/workers")}
            className="h-auto py-6 bg-white hover:bg-gray-50 border border-gray-200 text-left justify-start shadow-sm"
            data-testid="quick-action-workers"
          >
            <Users className="mr-4 h-6 w-6 text-[#F5A623]" />
            <div>
              <p className="font-semibold text-gray-800">Gerenciar Trabalhadores</p>
              <p className="text-xs text-gray-500 font-normal">Adicionar, editar ou remover</p>
            </div>
          </Button>

          <Button
            onClick={() => navigate("/admin/locations")}
            className="h-auto py-6 bg-white hover:bg-gray-50 border border-gray-200 text-left justify-start shadow-sm"
            data-testid="quick-action-locations"
          >
            <MapPin className="mr-4 h-6 w-6 text-[#2ECC40]" />
            <div>
              <p className="font-semibold text-gray-800">Gerenciar Locais</p>
              <p className="text-xs text-gray-500 font-normal">Criar QR codes e GPS</p>
            </div>
          </Button>

          <Button
            onClick={() => navigate("/admin/records")}
            className="h-auto py-6 bg-white hover:bg-gray-50 border border-gray-200 text-left justify-start shadow-sm"
            data-testid="quick-action-records"
          >
            <Clock className="mr-4 h-6 w-6 text-blue-500" />
            <div>
              <p className="font-semibold text-gray-800">Registros de Ponto</p>
              <p className="text-xs text-gray-500 font-normal">Ver histórico e adicionar manual</p>
            </div>
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
